import { AccountInfo, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import { Authenticator, LoginResult, User, UserNameType } from './authenticator';
import { savePostLoginRedirect } from './post-login-redirect';

export interface AzureB2CConfig {
  clientId: string;
  tenantId?: string;
  tenantName: string;
  policies: string[] | string;
  policyDisplayNames?: Record<string, string>;
  redirectUri: string;
  logoutRedirectUri?: string;
  scopes: string[] | string;
  /**
   * Enables the account/token debug log. Can also be switched on at runtime, in a dev
   * build or an e2e run, with `localStorage.setItem('azureB2CDebug', 'true')`.
   */
  debug?: boolean;
}

let globalAzureB2CConfig: AzureB2CConfig | undefined;
const ACTIVE_POLICY_STORAGE_KEY = 'azureB2CActivePolicy';
const CURRENT_LOGIN_POLICY_KEY = 'azureB2CCurrentLoginPolicy';
/**
 * homeAccountId of the account this browser's session was established with.
 *
 * MSAL keeps every account it has ever seen in its cache and `getAllAccounts()` returns
 * them in no guaranteed order, so selecting `accounts[0]` binds the session to whichever
 * account the cache happens to list first — the "signed in as the wrong user after
 * switching accounts" class of bug. The homeAccountId is stable per identity, so
 * recording it lets a reload restore exactly the account the user logged in as.
 */
const ACTIVE_ACCOUNT_STORAGE_KEY = 'azureB2CActiveAccountId';
const DEBUG_STORAGE_KEY = 'azureB2CDebug';
const AADB2C_SERVER_ERROR_CODE = 'AADB2C90075';
/**
 * How long before a token actually expires we start acquiring a new one.
 *
 * Matches MSAL's own `tokenRenewalOffsetSeconds` default, so a token this cache still
 * considers usable is one MSAL would also still serve from its cache.
 */
const TOKEN_RENEWAL_OFFSET_MS = 5 * 60 * 1000;

/** A bearer token held for reuse until it nears expiry. */
interface CachedToken {
  /** The identity the token was minted for; a token is never reused across accounts. */
  homeAccountId: string;
  token: string;
  /** Epoch milliseconds at which the token stops being valid. */
  expiresAt: number;
}

export function configureAzureB2C(config: AzureB2CConfig) {
  globalAzureB2CConfig = {
    ...config,
    scopes: normalizeScopes(config.scopes)
  };
}

export type AzureB2CAccountErrorReason =
  /** More than one cached account and nothing that identifies which one owns the session. */
  | 'ambiguous'
  /** The cache no longer holds the account the session was established with. */
  | 'mismatch';

/**
 * Raised when the active account cannot be resolved to a single, known identity.
 *
 * Thrown rather than resolved by falling back to another cached account: silently
 * continuing with a different identity is what produces requests signed as the wrong
 * user. Callers that cannot fail (`isAuthenticated`, `logout`) record it instead, so the
 * user ends up back at the login page, where a fresh redirect re-establishes an explicit
 * account.
 */
export class AzureB2CAccountError extends Error {
  constructor(
    readonly reason: AzureB2CAccountErrorReason,
    message: string
  ) {
    super(message);
    this.name = 'AzureB2CAccountError';
  }
}

export class AzureB2CAuthenticator extends Authenticator {
  private readonly pca: PublicClientApplication;
  private readonly scopes: string[];
  private readonly initialized: Promise<void>;
  private readonly policies: string[];
  private readonly policyDisplayNames: Record<string, string>;
  private readonly primaryPolicy: string;
  private readonly tenantName: string;
  private readonly tenantId?: string;
  private readonly debugEnabled: boolean;

  /** Set when the MSAL bootstrap (initialize + redirect handling) failed. */
  private initializationError: Error | null = null;
  /** Set when account resolution failed in a caller that cannot throw. */
  private accountError: AzureB2CAccountError | null = null;

  /**
   * The token last acquired, reused until it nears expiry.
   *
   * Every API request asks the authenticator for a bearer token, so without this the app
   * calls `acquireTokenSilent` once per request. That is only free while MSAL can serve
   * the request from its own cache -- and it cannot when B2C returns no access token for
   * the configured scopes (the `idToken` fallback below), because MSAL only caches an
   * access token. Each call then becomes a refresh-token round trip to B2C.
   */
  private cachedToken: CachedToken | null = null;
  /** The acquisition currently in flight, so parallel requests share one round trip. */
  private pendingToken: { homeAccountId: string; promise: Promise<string | null> } | null = null;

  constructor(config?: AzureB2CConfig) {
    super();
    const effectiveConfig: AzureB2CConfig | undefined = config ?? globalAzureB2CConfig;
    if (!effectiveConfig) {
      throw new Error('Azure B2C configuration missing');
    }

    this.policies = normalizePolicies(effectiveConfig.policies);
    this.policyDisplayNames = effectiveConfig.policyDisplayNames || {};
    this.tenantName = effectiveConfig.tenantName;
    this.tenantId = effectiveConfig.tenantId;
    this.primaryPolicy = this.policies[0];
    this.debugEnabled = effectiveConfig.debug === true || readStorage(DEBUG_STORAGE_KEY) === 'true';
    const authority = this.buildAuthority(this.primaryPolicy);

    this.pca = new PublicClientApplication({
      auth: {
        clientId: effectiveConfig.clientId,
        authority: authority,
        redirectUri: effectiveConfig.redirectUri,
        postLogoutRedirectUri: effectiveConfig.logoutRedirectUri ?? effectiveConfig.redirectUri,
        navigateToLoginRequestUrl: false,
        knownAuthorities: [`${effectiveConfig.tenantName}.b2clogin.com`]
      },
      cache: {
        cacheLocation: 'localStorage'
      }
    });
    this.scopes = ensureAuthScopes(normalizeScopes(effectiveConfig.scopes));

    this.initialized = this.pca
      .initialize()
      .then(async () => {
        await this.processRedirectWithRetry();
      })
      .catch((error: unknown) => {
        // Recorded and logged rather than swallowed: an unreported bootstrap failure is
        // indistinguishable from "not logged in", which is how a broken callback used to
        // present itself as a silent bounce back to the login page.
        this.initializationError = error instanceof Error ? error : new Error(String(error));
        console.error('[Azure B2C] Authentication bootstrap failed:', error);
      });
  }

  private async ensureInitialized(): Promise<void> {
    await this.initialized;
  }

  /**
   * The last auth/account-state error, for callers that want to surface why the session
   * is not usable (the login page, an error banner, an e2e assertion).
   */
  getAuthError(): Error | null {
    return this.accountError ?? this.initializationError;
  }

  private debug(message: string, context?: Record<string, unknown>): void {
    if (!this.debugEnabled) {
      return;
    }
    console.debug(`[Azure B2C] ${message}`, context ?? {});
  }

  private async processRedirectWithRetry(): Promise<void> {
    // Check for errors in URL first
    const errorInfo = this.extractErrorFromUrl();
    if (errorInfo) {
      console.error('[Azure B2C] Error in redirect URL:', errorInfo);
      // Clear error from URL to prevent retry loops
      this.clearErrorFromUrl();
      // For certain errors, we might want to continue instead of throwing
      // AADB2C90075 is a server error that might be transient
      if (errorInfo.errorCode === AADB2C_SERVER_ERROR_CODE) {
        console.warn('[Azure B2C] Server error detected, clearing state and continuing');
        return;
      }
      throw new Error(`Azure B2C error: ${errorInfo.errorCode} - ${errorInfo.errorDescription || errorInfo.error}`);
    }

    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const result = await this.pca.handleRedirectPromise();
        if (result?.account) {
          // The account that just completed the flow is the one the session belongs to,
          // so it is set explicitly and becomes the stored identity for later reloads.
          this.setActiveAccount(result.account, 'redirect-response');
          return;
        }

        // No redirect response: this is a plain reload, so restore the account the
        // session was established with instead of whatever the cache lists first.
        if (this.tryResolveActiveAccount()) {
          return;
        }

        if (!window.location.hash.includes('code=') && !window.location.search.includes('code=')) {
          return;
        }

        await new Promise((r) => setTimeout(r, attempt * 200));
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const errorCode = this.extractErrorCode(errorMessage);

        console.error(`[Azure B2C] Error processing redirect (attempt ${attempt}/${maxAttempts}):`, error);

        // Handle specific error codes
        if (errorCode === AADB2C_SERVER_ERROR_CODE) {
          // Server error - might be transient, try to recover
          if (attempt < maxAttempts) {
            console.warn('[Azure B2C] Server error, retrying...');
            await new Promise((r) => setTimeout(r, attempt * 500));
            continue;
          }
          console.error('[Azure B2C] Server error persisted after retries, clearing error state');
          this.clearErrorFromUrl();
          return;
        }

        if (attempt === maxAttempts) {
          throw error;
        }

        // Wait before retry
        await new Promise((r) => setTimeout(r, attempt * 200));
      }
    }
  }

  private extractErrorFromUrl(): { error: string; errorDescription?: string; errorCode?: string } | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const hash = window.location.hash;
    const search = window.location.search;
    const urlParams = new URLSearchParams(hash.substring(1) + (search ? '&' + search.substring(1) : ''));

    const error = urlParams.get('error');
    if (!error) {
      return null;
    }

    const errorDescription = urlParams.get('error_description') || undefined;
    return {
      error,
      errorDescription: errorDescription || undefined,
      errorCode: errorDescription ? this.extractErrorCode(errorDescription) : undefined
    };
  }

  private extractErrorCode(errorMessage: string): string | undefined {
    // Extract error codes like AADB2C90075 from error messages
    const match = errorMessage.match(/AADB2C\d+/);
    return match ? match[0] : undefined;
  }

  private clearErrorFromUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('error');
      url.searchParams.delete('error_description');
      url.searchParams.delete('error_code');
      url.searchParams.delete('correlation_id');

      // Use replaceState to avoid adding to history
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      console.warn('[Azure B2C] Failed to clear error from URL:', e);
    }
  }

  /**
   * Resolves the account that owns this session, deterministically:
   *
   *  1. the stored homeAccountId — the identity the session was established with, and the
   *     authority on the question;
   *  2. the account MSAL holds as active, for a session that predates the stored key,
   *     which is then adopted as the stored identity;
   *  3. the single cached account, when that is all there is;
   *  4. the policy the user just logged in with, or last used, when it singles one out.
   *
   * Anything left over is ambiguous and throws. No branch selects by array index, and no
   * branch substitutes a different identity for the stored one.
   *
   * @throws AzureB2CAccountError when the account is ambiguous or no longer cached.
   */
  private resolveActiveAccount(): AccountInfo | null {
    const accounts = this.pca.getAllAccounts();
    if (accounts.length === 0) {
      return null;
    }

    const activeAccount = this.pca.getActiveAccount();
    const storedAccountId = readStorage(ACTIVE_ACCOUNT_STORAGE_KEY);

    if (storedAccountId) {
      // Checked before MSAL's own active account, which can be left over from an earlier
      // session: the identity the app recorded is the one the session belongs to.
      if (activeAccount?.homeAccountId === storedAccountId) {
        return activeAccount;
      }

      const storedAccount = accounts.find((account) => account.homeAccountId === storedAccountId);
      if (storedAccount) {
        this.setActiveAccount(storedAccount, 'stored-identity');
        return storedAccount;
      }

      // The session's identity is gone from the cache. Continuing with one of the
      // remaining accounts would silently swap the user, so this is reported instead.
      throw new AzureB2CAccountError(
        'mismatch',
        `The account this session was established with is no longer cached (${accounts.length} other account(s) present). Sign in again.`
      );
    }

    if (activeAccount) {
      // A session from before this key existed. Adopting it now makes every later reload
      // resolve the same way instead of depending on MSAL's cache state.
      this.setActiveAccount(activeAccount, 'msal-active-account');
      return activeAccount;
    }

    if (accounts.length === 1) {
      this.setActiveAccount(accounts[0], 'single-cached-account');
      return accounts[0];
    }

    // Several accounts and no stored identity: the policy the user is signing in with
    // (or last signed in with) is the only remaining stable discriminator.
    const policy = readStorage(CURRENT_LOGIN_POLICY_KEY) ?? readStorage(ACTIVE_POLICY_STORAGE_KEY);
    if (policy) {
      const accountsForPolicy = accounts.filter(
        (account) => this.extractPolicyFromAccount(account)?.toLowerCase() === policy.toLowerCase()
      );
      if (accountsForPolicy.length === 1) {
        this.setActiveAccount(accountsForPolicy[0], 'policy-match');
        return accountsForPolicy[0];
      }
    }

    throw new AzureB2CAccountError(
      'ambiguous',
      `Cannot determine which of ${accounts.length} cached accounts owns this session. Sign in again.`
    );
  }

  /**
   * {@link resolveActiveAccount} for callers that must not throw. The error is recorded
   * (see {@link getAuthError}) and logged instead of being papered over.
   */
  private tryResolveActiveAccount(): AccountInfo | null {
    try {
      const account = this.resolveActiveAccount();
      if (account) {
        this.accountError = null;
      }
      return account;
    } catch (error: unknown) {
      if (error instanceof AzureB2CAccountError) {
        this.accountError = error;
        console.error(`[Azure B2C] Account state unusable (${error.reason}):`, error.message);
        return null;
      }
      throw error;
    }
  }

  private setActiveAccount(account: AccountInfo, source: string): void {
    const previous = this.pca.getActiveAccount();
    if (this.cachedToken && this.cachedToken.homeAccountId !== account.homeAccountId) {
      this.forgetToken();
    }
    this.pca.setActiveAccount(account);
    writeStorage(ACTIVE_ACCOUNT_STORAGE_KEY, account.homeAccountId);
    const policy = this.getPolicyForAccount(account);
    this.rememberActivePolicy(policy);
    // The pending login policy has served its purpose once an account is established.
    removeStorage(CURRENT_LOGIN_POLICY_KEY);
    this.accountError = null;

    this.debug('active account set', {
      source,
      policy,
      homeAccountId: account.homeAccountId,
      username: account.username,
      previousHomeAccountId: previous?.homeAccountId
    });
  }

  private buildAuthority(policy: string): string {
    return `https://${this.tenantName}.b2clogin.com/${this.tenantName}.onmicrosoft.com/${policy}`;
  }

  private extractPolicyFromAccount(account: AccountInfo): string | null {
    const claims = (account.idTokenClaims ?? {}) as { tfp?: string; acr?: string };
    return claims.tfp || claims.acr || null;
  }

  private getPolicyForAccount(account: AccountInfo): string {
    const policyFromClaims = this.extractPolicyFromAccount(account);

    if (policyFromClaims) {
      const matchingPolicy = this.policies.find((p) => p.toLowerCase() === policyFromClaims.toLowerCase());
      if (matchingPolicy) {
        return matchingPolicy;
      }
      console.warn('[Azure B2C] Policy not recognized:', policyFromClaims, 'using primary:', this.primaryPolicy);
    }

    return this.primaryPolicy;
  }

  private rememberActivePolicy(policy: string): void {
    writeStorage(ACTIVE_POLICY_STORAGE_KEY, policy);
  }

  /**
   * Returns a bearer token for one explicit account, reusing the token already held
   * until it nears expiry.
   *
   * Concurrent callers share a single acquisition: a page that fires several API
   * requests at once must not turn into several token requests.
   */
  private async acquireTokenForAccount(account: AccountInfo): Promise<string | null> {
    const cached = this.cachedToken;
    if (cached && cached.homeAccountId === account.homeAccountId && !isNearExpiry(cached.expiresAt)) {
      return cached.token;
    }

    const pending = this.pendingToken;
    if (pending && pending.homeAccountId === account.homeAccountId) {
      return pending.promise;
    }

    const promise = this.requestTokenForAccount(account);
    this.pendingToken = { homeAccountId: account.homeAccountId, promise };
    try {
      return await promise;
    } finally {
      if (this.pendingToken?.promise === promise) {
        this.pendingToken = null;
      }
    }
  }

  /**
   * Acquires a token for one explicit account. Never falls back to another account: a
   * token minted for a different identity than the caller expects is worse than no token.
   */
  private async requestTokenForAccount(account: AccountInfo): Promise<string | null> {
    const policyForAccount = this.getPolicyForAccount(account);
    const authority = this.buildAuthority(policyForAccount);

    try {
      const result = await this.pca.acquireTokenSilent({
        account,
        scopes: this.scopes,
        authority
      });

      if (result.account && result.account.homeAccountId !== account.homeAccountId) {
        throw new AzureB2CAccountError(
          'mismatch',
          `Token was issued for a different account than requested (expected ${account.homeAccountId}, got ${result.account.homeAccountId}).`
        );
      }

      this.debug('token acquired', {
        homeAccountId: account.homeAccountId,
        policy: policyForAccount,
        fromCache: result.fromCache,
        expiresOn: result.expiresOn?.toISOString()
      });

      const token = result.accessToken || result.idToken || '';
      this.rememberToken(account, token, result.expiresOn);

      return token;
    } catch (error: unknown) {
      if (error instanceof AzureB2CAccountError) {
        throw error;
      }

      if (error instanceof InteractionRequiredAuthError) {
        // Expected end of a session: the refresh token is gone or B2C wants the user
        // back. Re-authenticate the *same* account rather than dropping into an account
        // picker that could come back as someone else.
        this.debug('interaction required, re-authenticating the same account', {
          homeAccountId: account.homeAccountId,
          errorCode: error.errorCode
        });
        this.forgetToken();
        // This redirect happens mid-session (outside the nav guard), so remember
        // the current page here to return to it after re-authentication.
        if (typeof window !== 'undefined') {
          savePostLoginRedirect(window.location.pathname + window.location.search);
        }
        await this.pca.loginRedirect({
          scopes: this.scopes,
          authority,
          account,
          loginHint: account.username
        });
        return null;
      }

      // Anything else (network, configuration, B2C outage) is a real failure. It is
      // raised so the app can show it, instead of bouncing the user through a login
      // redirect that will fail again for the same reason.
      console.error('[Azure B2C] Silent token acquisition failed for the active account:', error);
      throw error;
    }
  }

  /**
   * Records a token for reuse, keyed to the account it was minted for.
   *
   * The expiry is read from the token itself first: `expiresOn` describes the access
   * token, so it says nothing about an id token returned in its place. A token whose
   * expiry cannot be established is used once and not cached, which is the previous
   * behaviour rather than a guess at how long it lasts.
   */
  private rememberToken(account: AccountInfo, token: string, expiresOn?: Date | null): void {
    const expiresAt = readTokenExpiry(token) ?? expiresOn?.getTime() ?? null;
    if (!token || expiresAt === null || isNearExpiry(expiresAt)) {
      this.cachedToken = null;
      return;
    }

    this.cachedToken = { homeAccountId: account.homeAccountId, token, expiresAt };
    this.debug('token cached', {
      homeAccountId: account.homeAccountId,
      expiresAt: new Date(expiresAt).toISOString()
    });
  }

  private forgetToken(): void {
    this.cachedToken = null;
    this.pendingToken = null;
  }

  async getUser(): Promise<User & { metadata: { azureB2CPolicy: string } }> {
    await this.ensureInitialized();
    const account = this.resolveActiveAccount();
    if (!account) {
      return { email: '', fullName: '', authorizationHeader: '', metadata: { azureB2CPolicy: '' } };
    }

    const accessToken = (await this.acquireTokenForAccount(account)) || '';
    const claims = account.idTokenClaims as {
      emails?: string[];
      email?: string;
      signInNames?: {
        emailAddress?: string;
      };
      name?: string;
      given_name?: string;
      family_name?: string;
    };

    const email = claims?.emails?.[0] || claims?.email || claims?.signInNames?.emailAddress || account.username || '';
    const name = claims?.name || '';
    const givenName = claims?.given_name || '';
    const familyName = claims?.family_name || '';
    const fullName = name || [givenName, familyName].filter(Boolean).join(' ') || email;

    return {
      email,
      fullName,
      authorizationHeader: accessToken ? `Bearer ${accessToken}` : '',
      metadata: {
        azureB2CPolicy: this.getPolicyForAccount(account)
      }
    };
  }

  async getAuthenticationHeader(): Promise<string> {
    const user = await this.getUser();
    return user.authorizationHeader;
  }

  async isAuthenticated(): Promise<boolean> {
    await this.ensureInitialized();
    // Cannot throw: the navigation guard turns an exception into the
    // "application not allowed" dead end, whereas an unusable account state should send
    // the user to the login page, where a redirect re-establishes an explicit account.
    return !!this.tryResolveActiveAccount();
  }

  async login(policy?: string, __?: string): Promise<LoginResult> {
    await this.ensureInitialized();
    const targetPolicy = policy || this.primaryPolicy;

    if (!this.policies.includes(targetPolicy)) {
      throw new Error(`Policy '${targetPolicy}' not found in configuration`);
    }

    // Remembered across the redirect: with several policies configured, the policy the
    // user picked here tells us which cached account the returning session belongs to.
    writeStorage(CURRENT_LOGIN_POLICY_KEY, targetPolicy);
    this.forgetToken();
    this.debug('login redirect', { policy: targetPolicy });

    await this.pca.loginRedirect({
      scopes: this.scopes,
      authority: this.buildAuthority(targetPolicy)
    });

    return { isLoggedIn: false, totpSetupDetails: { authenticatorCode: '', qrCodeUrl: '' } };
  }

  getAvailablePolicies(): string[] {
    return [...this.policies];
  }

  getPolicyDisplayName(policy: string): string {
    // First check if there's a configured display name
    if (this.policyDisplayNames[policy]) {
      return this.policyDisplayNames[policy];
    }

    // Fallback to auto-generated name
    return AzureB2CAuthenticator.generatePolicyDisplayName(policy);
  }

  static generatePolicyDisplayName(policy: string): string {
    // Remove the B2C prefix (e.g., "B2C_1_" or "B2C_1A_")
    let name = policy.replace(/^B2C_1[A-Z]?_/i, '');

    // Remove common flow prefixes like "signup_signin_", "signin_", "signup_"
    name = name.replace(/^(signup_signin_|signin_signup_|signin_|signup_|signupsignin_)/i, '');

    return name
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  userNameType(): UserNameType {
    return UserNameType.EMAIL;
  }

  requiresCredentials(): boolean {
    return false;
  }

  enableForgotPassword(): boolean {
    return false;
  }

  async forgotPassword(_username: string): Promise<void> {
    return Promise.reject(new Error('Not implemented'));
  }

  async forgotPasswordConfirm(_username: string, _password: string, _code: string): Promise<void> {
    return Promise.reject(new Error('Not implemented'));
  }

  async logout(): Promise<void> {
    await this.ensureInitialized();
    // Must not throw, and must not leave the identity keys behind: the next login has to
    // start from whatever account that flow returns, not from this session's.
    const account = this.tryResolveActiveAccount();
    this.forgetToken();
    removeStorage(ACTIVE_ACCOUNT_STORAGE_KEY);
    removeStorage(CURRENT_LOGIN_POLICY_KEY);
    this.debug('logout', { homeAccountId: account?.homeAccountId });
    await this.pca.logoutRedirect({ account: account ?? undefined });
  }

  getPasswordPolicy(): undefined {
    return undefined;
  }
}

function isNearExpiry(expiresAt: number): boolean {
  return expiresAt - Date.now() <= TOKEN_RENEWAL_OFFSET_MS;
}

/**
 * Reads the `exp` claim of a JWT, in epoch milliseconds.
 *
 * Returns null for anything that is not a readable JWT, so an opaque token is simply
 * never cached rather than cached with an invented lifetime.
 *
 * The payload is read byte-for-byte rather than decoded as UTF-8: `exp` is a number, so
 * multi-byte characters elsewhere in the claims cannot affect it.
 */
function readTokenExpiry(token: string): number | null {
  const payload = token.split('.')[1];
  if (!payload) {
    return null;
  }

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const claims = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof claims.exp === 'number' ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

function getStorage(): Storage | null {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch {
    // Accessing localStorage can throw (disabled cookies / privacy mode).
  }
  return null;
}

function readStorage(key: string): string | null {
  try {
    return getStorage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    getStorage()?.setItem(key, value);
  } catch {
    // Ignore storage errors (quota, privacy mode, ...).
  }
}

function removeStorage(key: string): void {
  try {
    getStorage()?.removeItem(key);
  } catch {
    // Ignore storage errors.
  }
}

function normalizeScopes(input?: string[] | string): string[] {
  if (!input) {
    return [];
  }
  if (Array.isArray(input)) {
    return input.map((s) => s.trim()).filter(Boolean);
  }
  return input
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function ensureAuthScopes(scopes: string[]): string[] {
  const set = new Set(scopes);
  set.add('openid');
  set.add('profile');
  return Array.from(set);
}

function normalizePolicies(input: string[] | string): string[] {
  if (Array.isArray(input)) {
    return input.map((s) => s.trim()).filter(Boolean);
  }
  return input
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}
